import { MutableRefObject, RefObject, useCallback, useEffect } from 'react';
import { OverlayScrollbarsComponentRef } from 'overlayscrollbars-react';
import { IAssetViewerStateContextValue } from '@context/AssetViewerStateContext';
import type { AssetViewerZoomOption } from '@context/AssetViewerStateContext/types';
import type { PdfPinchGestureState } from '@pages/MediaViewer/AssetViewer/PdfCard/hooks/useMobilePdfPinchZoom';

type UseMobilePdfZoomOptionParams = {
  wrapperRef: RefObject<OverlayScrollbarsComponentRef<'div'>>;
  pdfPinchGestureRef: MutableRefObject<PdfPinchGestureState>;
  fitZoomRef: MutableRefObject<number>;
  pageSize: { width: number; height: number };
  turnoverDegree: number;
  isPageSizeReady: boolean;
  zoomOption?: AssetViewerZoomOption;
  setZoomFactor: IAssetViewerStateContextValue['setZoomFactor'];
  setZoomOption: IAssetViewerStateContextValue['setZoomOption'];
};

export function useMobilePdfZoomOption({
  wrapperRef,
  pdfPinchGestureRef,
  fitZoomRef,
  pageSize,
  turnoverDegree,
  isPageSizeReady,
  zoomOption,
  setZoomFactor,
  setZoomOption
}: UseMobilePdfZoomOptionParams) {
  const getZoomFactorFromOption = useCallback(
    (option: NonNullable<AssetViewerZoomOption>) => {
      if (option === 'actual-size') return 1;
      const container = wrapperRef?.current?.getElement();
      if (!container || !isPageSizeReady) return 1;
      const width =
        Math.abs(pageSize.width * Math.cos(turnoverDegree * (Math.PI / 180))) +
        Math.abs(pageSize.height * Math.sin(turnoverDegree * (Math.PI / 180)));
      const height =
        Math.abs(pageSize.width * Math.sin(turnoverDegree * (Math.PI / 180))) +
        Math.abs(pageSize.height * Math.cos(turnoverDegree * (Math.PI / 180)));
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;
      const scaleWidth = Math.floor(containerWidth / (width / 100)) / 100;
      const scaleHeight = Math.floor(containerHeight / (height / 100)) / 100;
      if (option === 'auto' || option === 'fit-width') {
        return scaleWidth;
      }
      if (option === 'fit-height') {
        return scaleHeight;
      }
      return 1;
    },
    [wrapperRef, pageSize, turnoverDegree, isPageSizeReady]
  );

  useEffect(() => {
    setZoomOption('fit-width');
  }, [setZoomOption]);

  useEffect(() => {
    if (!zoomOption || !isPageSizeReady) return undefined;

    const applyFitZoom = () => {
      if (pdfPinchGestureRef.current.isActive) return;
      const newZoomFactor = getZoomFactorFromOption(zoomOption);
      if (zoomOption === 'fit-width' || zoomOption === 'auto') {
        fitZoomRef.current = newZoomFactor;
      }
      setZoomFactor(newZoomFactor, zoomOption);
    };

    applyFitZoom();
    window.addEventListener('resize', applyFitZoom);
    return () => window.removeEventListener('resize', applyFitZoom);
  }, [
    zoomOption,
    isPageSizeReady,
    getZoomFactorFromOption,
    setZoomFactor,
    pdfPinchGestureRef,
    fitZoomRef
  ]);
}
